import { apiClient } from '../config/api';
import { ENDPOINTS } from '../config/endpoints';
import { ApiResponse } from '../config/endpoints';
import { Cotacao } from '../types/cotacao';

// Re-export Cotacao type for convenience
export type { Cotacao } from '../types/cotacao';

// Comparison response from /comparar endpoint
export interface CotacaoComparacao {
  calculoId: string;
  totalCotacoes: number;
  melhorPreco?: {
    seguradora: string;
    premioTotal: number;
    economia: number;
  };
  maiorPreco?: {
    seguradora: string;
    premioTotal: number;
    diferencaParaMelhor: number;
  };
  maiorComissao?: {
    seguradora: string;
    percentualComis: number;
    valorComissao: number;
  };
  cotacoes: Array<{
    id?: string;
    seguradora: string;
    plano?: string;
    premioTotal: number;
    percentualComis?: number;
    valorComissao?: number;
    diferencaParaMelhor: number;
    selecionada: boolean;
  }>;
}

class CotacaoService {
  private readonly baseUrl = '/api/cotacao';

  /**
   * Lista todas as cotações de um cálculo específico
   */
  async listByCalculo(calculoId: string): Promise<ApiResponse<Cotacao[]>> {
    return apiClient.request<ApiResponse<Cotacao[]>>(
      `${this.baseUrl}/calculo/${calculoId}`
    );
  }

  /**
   * Busca uma cotação específica por ID
   */
  async getById(id: string): Promise<ApiResponse<Cotacao>> {
    return apiClient.request<ApiResponse<Cotacao>>(
      `${this.baseUrl}/${id}`
    );
  }

  /**
   * Busca a cotação selecionada de um cálculo
   */
  async getSelecionada(calculoId: string): Promise<ApiResponse<Cotacao>> {
    return apiClient.request<ApiResponse<Cotacao>>(
      `${this.baseUrl}/calculo/${calculoId}/selecionada`
    );
  }

  /**
   * Compara todas as cotações de um cálculo
   * Retorna análise de melhor preço, maior comissão, etc.
   */
  async comparar(calculoId: string): Promise<ApiResponse<CotacaoComparacao>> {
    return apiClient.request<ApiResponse<CotacaoComparacao>>(
      `${this.baseUrl}/calculo/${calculoId}/comparar`
    );
  }

  /**
   * Cria uma nova cotação
   */
  async create(data: Partial<Cotacao>): Promise<ApiResponse<Cotacao>> {
    return apiClient.request<ApiResponse<Cotacao>>(this.baseUrl, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Atualiza uma cotação existente
   */
  async update(id: string, data: Partial<Cotacao>): Promise<ApiResponse<Cotacao>> {
    return apiClient.request<ApiResponse<Cotacao>>(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Marca uma cotação como selecionada
   * Automaticamente desmarca outras cotações do mesmo cálculo
   */
  async selecionar(id: string): Promise<ApiResponse<Cotacao>> {
    return apiClient.request<ApiResponse<Cotacao>>(
      `${this.baseUrl}/${id}/selecionar`,
      {
        method: 'PUT',
      }
    );
  }

  /**
   * Deleta uma cotação
   */
  async delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.request<ApiResponse<boolean>>(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Lista todas as cotações (admin only)
   */
  async listAll(): Promise<ApiResponse<Cotacao[]>> {
    return apiClient.request<ApiResponse<Cotacao[]>>(this.baseUrl);
  }
}

export const cotacaoService = new CotacaoService();
